// Story BP-8 PC.4: drawer side-sliding (não full-page) com 4 seções:
// 1. User summary (email/id/created/plan/effective/grandfather/trial/exempt)
// 2. Subscription Stripe (status/price/period_end/cancel_at_period_end)
// 3. Override form (plan_tier + grandfather_until + reason required min 10)
// 4. Audit log recent (last 10 actions sobre esse target_user_id)
//
// PC.5: reason textarea REQUIRED min 10 chars — validação client + DB CHECK.
// PC.8: grandfather_until pode ser NULL com confirmation modal inline.

import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, ShieldCheck, AlertTriangle, History } from 'lucide-react';
import { Button } from '../Button';
import { Input } from '../ui/Input';
import {
  overridePlanTier,
  overrideGrandfather,
  fetchAuditLog,
  type AdminBillingUser,
  type AuditLogEntry,
} from '../../hooks/useAdminBilling';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import type { PlanTier } from '../../lib/plans/types';

export interface AdminBillingOverrideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminBillingUser | null;
  onSuccess?: () => void;
}

const TIER_OPTIONS: { value: PlanTier; label: string }[] = [
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'business', label: 'Business' },
];

const MIN_REASON = 10;

export const AdminBillingOverrideDrawer: React.FC<AdminBillingOverrideDrawerProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess,
}) => {
  const [newTier, setNewTier] = useState<PlanTier>('free');
  const [grandfatherDate, setGrandfatherDate] = useState<string>('');
  const [clearGrandfather, setClearGrandfather] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [audit, setAudit] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useLockBodyScroll(isOpen);

  // Reset form on open/user change
  useEffect(() => {
    if (!isOpen || !user) return;
    setNewTier(user.plan_tier);
    setGrandfatherDate(user.grandfather_until ? user.grandfather_until.slice(0, 10) : '');
    setClearGrandfather(false);
    setReason('');
    setSubmitError(null);
    setConfirmClearOpen(false);
  }, [isOpen, user?.id, user?.plan_tier, user?.grandfather_until]);

  // Fetch audit log for this user
  useEffect(() => {
    if (!isOpen || !user) return;
    let cancelled = false;
    setAuditLoading(true);
    fetchAuditLog({ targetUserId: user.id, limit: 10 }).then(({ data }) => {
      if (cancelled) return;
      setAudit(data);
      setAuditLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, user?.id]);

  // ESC to close (when not submitting)
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, submitting, onClose]);

  const tierChanged = user && newTier !== user.plan_tier;
  const grandfatherChanged = (() => {
    if (!user) return false;
    const currentDate = user.grandfather_until ? user.grandfather_until.slice(0, 10) : '';
    if (clearGrandfather) return user.grandfather_until !== null;
    return grandfatherDate !== currentDate;
  })();
  const hasAnyChange = !!tierChanged || grandfatherChanged;
  const reasonValid = reason.trim().length >= MIN_REASON;

  const handleSubmit = useCallback(async () => {
    if (!user || !hasAnyChange || !reasonValid || submitting) return;

    // PC.8: confirmation antes de clear grandfather permanente
    if (clearGrandfather && !confirmClearOpen) {
      setConfirmClearOpen(true);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      if (tierChanged) {
        const { error } = await overridePlanTier(user.id, newTier, reason.trim());
        if (error) {
          setSubmitError(error);
          setSubmitting(false);
          return;
        }
      }
      if (grandfatherChanged) {
        const newExpiry = clearGrandfather
          ? null
          : grandfatherDate
            ? new Date(grandfatherDate).toISOString()
            : null;
        const { error } = await overrideGrandfather(user.id, newExpiry, reason.trim());
        if (error) {
          setSubmitError(error);
          setSubmitting(false);
          return;
        }
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setSubmitting(false);
    }
  }, [
    user,
    hasAnyChange,
    reasonValid,
    submitting,
    clearGrandfather,
    confirmClearOpen,
    tierChanged,
    newTier,
    reason,
    grandfatherChanged,
    grandfatherDate,
    onSuccess,
    onClose,
  ]);

  if (!isOpen || !user) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
        onClick={submitting ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Override plan tier"
        className="fixed top-0 right-0 h-full w-full sm:w-[480px] z-50 bg-neutral-950 border-l border-white/10 shadow-2xl overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-neutral-950/95 backdrop-blur-sm border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary-400" />
              <h2 className="text-sm font-semibold text-white">Override billing</h2>
            </div>
            <p className="text-xs text-neutral-500 mt-0.5">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 disabled:opacity-50"
            aria-label="Fechar drawer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Section 1: User summary */}
          <section>
            <SectionTitle>Resumo do usuário</SectionTitle>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
              <SummaryItem label="ID">
                <span className="font-mono text-[10px]">{user.id.slice(0, 12)}…</span>
              </SummaryItem>
              <SummaryItem label="Criado em">
                {new Date(user.created_at).toLocaleDateString('pt-BR')}
              </SummaryItem>
              <SummaryItem label="Tier (raw)">{user.plan_tier}</SummaryItem>
              <SummaryItem label="Tier (efetivo)">{user.effective_tier}</SummaryItem>
              <SummaryItem label="Grandfather até">
                {user.grandfather_until
                  ? new Date(user.grandfather_until).toLocaleDateString('pt-BR')
                  : '—'}
              </SummaryItem>
              <SummaryItem label="Trial até">
                {user.trial_ends_at
                  ? new Date(user.trial_ends_at).toLocaleDateString('pt-BR')
                  : '—'}
              </SummaryItem>
              <SummaryItem label="Is exempt">{user.is_exempt ? 'Sim' : 'Não'}</SummaryItem>
              <SummaryItem label="Subscription">
                {user.subscription_status ?? 'sem subscription'}
              </SummaryItem>
            </div>
          </section>

          {/* Section 2: Override form */}
          <section>
            <SectionTitle>Aplicar override</SectionTitle>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-neutral-400 mb-1.5">Plan tier</label>
                <select
                  value={newTier}
                  onChange={e => setNewTier(e.target.value as PlanTier)}
                  disabled={submitting}
                  className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                >
                  {TIER_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-neutral-400 mb-1.5">
                  Grandfather até
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={clearGrandfather ? '' : grandfatherDate}
                    onChange={e => {
                      setGrandfatherDate(e.target.value);
                      setClearGrandfather(false);
                    }}
                    disabled={submitting || clearGrandfather}
                  />
                  <label className="inline-flex items-center gap-1.5 text-xs text-neutral-400 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={clearGrandfather}
                      onChange={e => {
                        setClearGrandfather(e.target.checked);
                        if (e.target.checked) setGrandfatherDate('');
                        setConfirmClearOpen(false);
                      }}
                      disabled={submitting}
                      className="w-3.5 h-3.5 rounded border-neutral-600 bg-neutral-800 text-primary-500"
                    />
                    Remover
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs text-neutral-400 mb-1.5">
                  Motivo (mín. {MIN_REASON} caracteres) *
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  disabled={submitting}
                  rows={3}
                  placeholder="Ex: comped — deal fechado com Acme Corp (ticket #1234)"
                  className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:ring-1 focus:ring-primary-500/50"
                />
                <p
                  className={`text-[11px] mt-1 ${reason.trim().length < MIN_REASON ? 'text-neutral-500' : 'text-green-400'}`}
                >
                  {reason.trim().length}/{MIN_REASON} caracteres mínimos
                </p>
              </div>

              {confirmClearOpen && clearGrandfather && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-200 mb-1">
                      Confirmar remoção permanente do grandfather
                    </p>
                    <p>
                      Esse usuário perderá imediatamente o acesso herdado. Ação registrada no
                      audit log. Clique em "Aplicar override" de novo pra confirmar.
                    </p>
                  </div>
                </div>
              )}

              {submitError && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400">
                  {submitError}
                </div>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!hasAnyChange || !reasonValid || submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 min-h-[40px]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Aplicando...
                  </>
                ) : confirmClearOpen && clearGrandfather ? (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    Confirmar remoção
                  </>
                ) : (
                  'Aplicar override'
                )}
              </button>
            </div>
          </section>

          {/* Section 3: Audit log */}
          <section>
            <SectionTitle>
              <History className="w-3.5 h-3.5 inline mr-1.5" />
              Histórico recente
            </SectionTitle>
            {auditLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 text-neutral-500 animate-spin" />
              </div>
            ) : audit.length === 0 ? (
              <p className="text-xs text-neutral-500 italic">Nenhuma ação registrada.</p>
            ) : (
              <ul className="space-y-2">
                {audit.map(e => (
                  <AuditEntry key={e.id} entry={e} />
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-neutral-950/95 backdrop-blur-sm border-t border-white/10 px-5 py-3">
          <Button variant="outline" fullWidth onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
        </div>
      </aside>
    </>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-3">
    {children}
  </h3>
);

const SummaryItem: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-neutral-500 text-[10px] uppercase tracking-wide">{label}</div>
    <div className="text-white">{children}</div>
  </div>
);

const ACTION_LABEL: Record<string, string> = {
  override_plan_tier: 'Override plan tier',
  override_grandfather_until: 'Override grandfather',
  reset_quota: 'Reset quota',
  revoke_exempt: 'Revoke exempt',
  grant_exempt: 'Grant exempt',
};

const AuditEntry: React.FC<{ entry: AuditLogEntry }> = ({ entry }) => (
  <li className="p-2.5 rounded-lg border border-white/5 bg-neutral-900/30 text-xs">
    <div className="flex items-center justify-between gap-2 mb-1">
      <span className="font-medium text-white">{ACTION_LABEL[entry.action] ?? entry.action}</span>
      <span className="text-neutral-500 text-[10px] font-mono">
        {new Date(entry.created_at).toLocaleString('pt-BR')}
      </span>
    </div>
    {entry.reason && (
      <p className="text-neutral-400 italic">"{entry.reason}"</p>
    )}
  </li>
);
